<template>
  <div class="td-page">
    <!-- ============ HEADER ============ -->
    <div class="td-header">
      <div class="td-header-left">
        <el-button size="mini" type="primary" icon="el-icon-back" plain class="td-back-btn" @click="goBack">返回</el-button>
        <span class="td-brand-sub">教师学情驾驶舱</span>
      </div>
      <div class="td-header-right">
        <el-select v-model="selectedClass" size="mini" class="class-select" popper-class="dark-popper" @change="onClassChange">
          <el-option v-for="cls in classList" :key="cls.id" :label="cls.label" :value="cls.id"></el-option>
        </el-select>
        <span class="td-header-date">{{ todayDate }}</span>
      </div>
    </div>

    <!-- ============ MAIN 3-COLUMN GRID ============ -->
    <div class="td-grid">
      <!-- =============== LEFT COLUMN =============== -->
      <div class="td-col td-col-left">

        <!-- Class selector -->
        <div class="td-card td-card-class">
          
          <div class="td-class-main">
            <span class="td-class-name">{{ currentClassName }}</span>
            <div class="td-class-count">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(240,244,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              <span class="td-class-count-txt">{{ currentStudentCount }}人</span>
            </div>
          </div>
        </div>

        <!-- Core indicators -->
        <div class="td-card">
          <div class="td-title">班级核心指标</div>
          <div class="core-grid">
            <div class="core-item core-cyan">
              <span class="core-val">{{ kpiData.avg.toFixed(1) }}</span>
              <span class="core-lbl">平均分</span>
            </div>
            <div class="core-item core-green">
              <span class="core-val">{{ kpiData.passRate.toFixed(1) }}%</span>
              <span class="core-lbl">及格率</span>
            </div>
            <div class="core-item core-gold">
              <span class="core-val">{{ kpiData.excellentRate.toFixed(1) }}%</span>
              <span class="core-lbl">优秀率</span>
            </div>
            <div class="core-item core-red">
              <span class="core-val">{{ kpiData.lowRate.toFixed(1) }}%</span>
              <span class="core-lbl">低分率</span>
            </div>
          </div>
        </div>

        <!-- Trend alerts -->
        <div class="td-card">
          <div class="td-title">学情趋势预警</div>
          <div class="trend-row">
            <div class="trend-item trend-up">
              <div class="trend-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00e676" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              </div>
              <div class="trend-text">
                <span class="trend-lbl">进步最快</span>
                <span class="trend-num trend-num-up">{{ trendsData.up }}人</span>
              </div>
            </div>
            <div class="trend-item trend-down">
              <div class="trend-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff4d6d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
              </div>
              <div class="trend-text">
                <span class="trend-lbl">退步最快</span>
                <span class="trend-num trend-num-down">{{ trendsData.down }}人</span>
              </div>
            </div>
            <div class="trend-item trend-flat">
              <div class="trend-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffb400" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              </div>
              <div class="trend-text">
                <span class="trend-lbl">停滞不前</span>
                <span class="trend-num trend-num-flat">{{ trendsData.stable }}人</span>
              </div>
            </div>
          </div>

          <!-- Diagnosis -->
          <div class="diag-block">
            <div class="td-title td-title-sm">班级学情诊断</div>
            <div class="diag-band">
              <div class="diag-dot" :style="{ background: diagnosisData.statusColor }"></div>
              <span class="diag-band-txt" :style="{ color: diagnosisData.statusColor }">{{ diagnosisData.status }}</span>
            </div>
            <div class="diag-seg">
              <span class="diag-seg-txt">{{ diagnosisData.summary }}</span>
            </div>
            <div class="diag-hint">
              <div class="diag-hint-dot"></div>
              <span class="diag-hint-txt">{{ diagnosisData.suggestion }}</span>
            </div>
          </div>
        </div>

        <!-- TOP3 weakness -->
        <div class="td-card">
          <div class="td-title">题型短板 TOP3</div>
          <div class="top3-list">
            <div class="top3-item" :class="{'top3-item-active': activeTop3Idx === i}" @click="onTop3Click(item, i)" v-for="(item, i) in top3Data" :key="i">
              <div class="top3-rank" :class="'top3-rank-' + (i + 1)">{{ i + 1 }}</div>
              <span class="top3-name">{{ item.name }}</span>
              <el-progress
                :percentage="item.score"
                :color="item.color"
                :stroke-width="6"
                :show-text="false"
                class="top3-progress"
              ></el-progress>
              <span class="top3-score" :class="item.score < 60 ? 'top3-score-red' : 'top3-score-gold'">{{ item.score }}</span>
            </div>
          </div>
        </div>

        <!-- Class dynamics -->
        <div class="td-card">
          <div class="td-title">班级动态</div>
          <div class="dyn-grid">
            <div class="dyn-item" v-for="d in dynamics" :key="d.label">
              <div class="dyn-val-row">
                <span class="dyn-val dyn-val-big">{{ d.value }}</span>
                <span class="dyn-delta" v-if="d.delta">{{ d.delta }}</span>
              </div>
              <span class="dyn-lbl">{{ d.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- =============== MIDDLE COLUMN =============== -->
      <div class="td-col td-col-mid">

        <!-- Level distribution -->
        <div class="td-card td-card-chart">
          <div class="td-title">阅读等级分布图</div>
          <div ref="levelChart" class="chart-box level-box"></div>
        </div>

        <!-- Weak point heatmap -->
        <div class="td-card td-card-chart">
          <div class="td-title">应试英语题型薄弱点热力图</div>
          <div ref="weakChart" class="chart-box weak-box"></div>
        </div>

        <!-- Ability radar -->
        <div class="td-card td-card-chart">
          <div class="td-title">班级能力雷达图</div>
          <div ref="radarChart" class="chart-box radar-box"></div>
        </div>
      </div>

      <!-- =============== RIGHT COLUMN =============== -->
      <div class="td-col td-col-right">

        <!-- Ability map -->
        <div class="td-card">
          <div class="td-title">题型能力图谱</div>
          <div class="am-list">
            <div class="am-block" :class="{'am-block-active': activeAbilityName === a.name}" @click="onAbilityClick(a)" v-for="a in abilities" :key="a.name">
              <div class="am-head">
                <span class="am-name">{{ a.name }}</span>
                <span class="am-score" :class="'am-score-' + a.color">{{ a.score }}分</span>
              </div>
              <div class="am-bars">
                <div
                  v-for="n in 10"
                  :key="n"
                  class="am-bar"
                  :class="n <= a.bars ? 'am-bar-' + a.color : ''"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Student tier list (collapsible) -->
        <div class="td-card stu-card">
          <div class="td-tier-section" v-for="(tier, ti) in studentTiers" :key="ti">
            <div class="td-tier-head" :class="{'td-tier-head-collapsed': tier.collapsed}" @click="toggleTier(tier)">
              <div class="td-tier-title-wrap">
                <span class="td-tier-arrow" :class="{'td-tier-arrow-rotated': tier.collapsed}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(240,244,255,0.7)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </span>
                <span class="td-tier-title">{{ tier.title }}</span>
                <el-tag size="mini" :type="tier.tagType" effect="dark">{{ tier.students.length }}人</el-tag>
              </div>
            </div>
            <transition name="tier-slide">
              <div class="td-student-list" v-show="!tier.collapsed">
                <div class="td-stu-item" v-for="(s, i) in tier.students" :key="ti + '-' + i">
                  <div class="td-stu-avatar" :class="'td-stu-avatar-' + ((i % 9) + 1)">
                    <span class="td-stu-avatar-txt">{{ s.avatar }}</span>
                  </div>
                  <div class="td-stu-mid">
                    <span class="td-stu-name">{{ s.name }}</span>
                    <el-tag size="mini" :type="s.tagType" effect="dark">{{ s.tag }}</el-tag>
                  </div>
                  <span class="td-stu-score" :class="s.score < 60 ? 'td-stu-score-red' : 'td-stu-score-gold'">{{ s.score }}</span>
                  <el-button size="mini" type="text" class="td-stu-detail" @click="openStudentDetail(s, i)">查看详情</el-button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ STUDENT DETAIL DIALOG ============ -->
    <el-dialog
      :visible.sync="studentDialogVisible"
      width="560px"
      custom-class="student-detail-dialog"
      :show-close="true"
      :close-on-click-modal="true"
      append-to-body
      :before-close="closeStudentDialog"
    >
      <template slot="default">
        <div class="sd-header">
          <div class="sd-avatar" :class="currentStudent.avatar ? 'sd-avatar-' + currentStudent.avatar : 'sd-avatar-default'">
            <span class="sd-avatar-txt">{{ currentStudent.avatar }}</span>
          </div>
          <div class="sd-head-info">
            <div class="sd-head-row1">
              <span class="sd-name">{{ currentStudent.name }}</span>
              <span class="sd-class">{{ currentStudent.className || currentClassName }}</span>
            </div>
            <div class="sd-head-row2">
              <span class="sd-score-label">综合分数</span>
              <span class="sd-score-val" :class="currentStudent.score < 60 ? 'sd-score-red' : 'sd-score-gold'">{{ currentStudent.score }}</span>
              <span class="sd-level-dot"></span>
              <span class="sd-level">等级 {{ currentStudent.level || 'Lv5' }}</span>
              <span class="sd-status-dot" :class="'sd-status-' + (currentStudent.status || 'normal')"></span>
              <span class="sd-status" :class="'sd-status-txt-' + (currentStudent.status || 'normal')">{{ currentStudent.tierLabel || currentStudent.status || '正常' }}</span>
            </div>
          </div>
          <span class="sd-close" @click="closeStudentDialog">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(240,244,255,0.5)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </span>
        </div>

        <!-- 各题型得分 -->
        <div class="sd-section">
          <div class="sd-section-title">各题型得分</div>
          <div class="sd-score-list">
            <div class="sd-score-item" v-for="(item, idx) in currentStudent.scores" :key="idx">
              <span class="sd-score-name">{{ item.name }}</span>
              <div class="sd-score-bar-wrap">
                <div class="sd-score-bar-bg"></div>
                <div class="sd-score-bar-fill" :style="{ width: item.score + '%', background: item.color }"></div>
              </div>
              <span class="sd-score-num" :style="{ color: item.color }">{{ item.score }}</span>
            </div>
          </div>
        </div>

        <!-- 近7天成绩趋势 -->
        <div class="sd-section">
          <div class="sd-section-title">近7天成绩趋势</div>
          <div ref="studentTrendChart" class="sd-trend-chart"></div>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import Http from "../../apis/teach";
export default {
  name: 'TeacherDashboard',
  data() {
    return {
      selectedClass: null,
      classList: [],
      todayDate: '',
      charts: [],
      kpiData: {
        avg: 0,
        passRate: 0,
        excellentRate: 0,
        lowRate: 0
      },
      trendsData: {
        up: 0,
        down: 0,
        stable: 0
      },
      diagnosisData: {
        status: '暂无数据',
        statusColor: '#64748B',
        summary: '',
        suggestion: ''
      },
      levelData: {
        distribution: [0, 0, 0, 0, 0, 0, 0],
        avgLevel: 0
      },
      weakData: [],
      radarData: [],

      // TOP3 weakness
      top3Data: [],

      // Class dynamics
      dynamics: [],

      // Ability map
      abilities: [
        { name: '听力理解', score: 78, level: 'Lv3', skill: '表达流利度', color: 'green', bars: 8, dim: '表达流利度' },
        { name: '阅读理解', score: 72, level: 'Lv3', skill: '逻辑连贯性', color: 'orange', bars: 7, dim: '逻辑连贯性' },
        { name: '词汇运用', score: 68, level: 'Lv2', skill: '词汇深度', color: 'gold', bars: 7, dim: '词汇深度' },
        { name: '语法填空', score: 58, level: 'Lv2', skill: '语法精准度', color: 'red', bars: 6, dim: '语法精准度' }
      ],

      // Student tiers (collapsible)
      studentTiers: [],

      // Dialog state
      studentDialogVisible: false,
      currentStudent: {},
      studentTrendChart: null,

      // Radar linkage state
      radarChart: null,
      radarDims: ['词汇深度', '表达流利度', '逻辑连贯性', '语法精准度'],
      radarBaseValues: [72, 68, 72, 65],
      radarCompareValues: [68, 64, 70, 61],
      activeRadarDim: '',
      activeRadarScore: null,
      activeTop3Idx: -1,
      activeAbilityName: '',
      plan_id: 0
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initLevelChart()
      this.initWeakChart()
      this.initRadarChart()
      window.addEventListener('resize', this.handleResize)
    })
  },
    created() {
        //this.plan_id = this.$route.query.plan_id

        var days = ['星期日','星期一','星期二','星期三','星期四','星期五','星期六']
        var now = new Date()
        this.todayDate = now.getFullYear() + '年' + (now.getMonth() + 1) + '月' + now.getDate() + '日 ' + days[now.getDay()]
        this.getTeacherClasses()

    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize)
        this.charts.forEach(function (c) { if (c) c.dispose() })
        if (this.studentTrendChart) {
            this.studentTrendChart.dispose()
            this.studentTrendChart = null
        }
    },

  computed: {
    currentClassName() {
      var item = this.classList.find(c => c.id === this.selectedClass)
      return item ? item.name : ''
    },
    currentStudentCount() {
      var item = this.classList.find(c => c.id === this.selectedClass)
      return item ? item.student_count : 0
    }
  },

  methods: {

    getTeacherClasses(){
        Http.teacherClasses().then((res) => {
          this.classList = res.data || []
          if (this.classList.length > 0) {
            this.selectedClass = this.classList[0].id

            // 核心指标
            this.getTeacherKpi(this.selectedClass)
            // 学情趋势预警
            this.getTeacherTrends(this.selectedClass)
            // 学情诊断
            this.getTeacherDiagnosis(this.selectedClass)
            // 题型短板TOP3
            this.getTeacherWeakTop3(this.selectedClass)
            // 班级动态
            this.getTeacherStats(this.selectedClass)
            //等级分布
            this.getTeacherLevelDistribution(this.selectedClass)  

            //题型薄弱点热力图
            this.getTeacherQuestionScores(this.selectedClass)
            //四维雷达图
            this.getTeacherRadar(this.selectedClass)

            //学生分层
            this.getTeacherStudentList(this.selectedClass)
          }
        });
    },
    goBack() {
      this.$router ? this.$router.back() : (window.history.length > 1 ? window.history.back() : this.$router.push('/'))
    },
    onClassChange() {
      if (!this.selectedClass) return
      // 核心指标
      this.getTeacherKpi(this.selectedClass)
      // 学情趋势预警
      this.getTeacherTrends(this.selectedClass)
      // 学情诊断
      this.getTeacherDiagnosis(this.selectedClass)
      // 题型短板TOP3
      this.getTeacherWeakTop3(this.selectedClass)
      // 班级动态
      this.getTeacherStats(this.selectedClass)
      //等级分布
      this.getTeacherLevelDistribution(this.selectedClass)
      //题型薄弱点热力图
      this.getTeacherQuestionScores(this.selectedClass)
      //四维雷达图
      this.getTeacherRadar(this.selectedClass)
      //学生分层
      this.getTeacherStudentList(this.selectedClass)
    },
    // 核心指标
    getTeacherKpi(plan_id){
        Http.teacherKpi({plan_id:plan_id}).then((res) => {
          this.kpiData = res.data || { avg: 0, passRate: 0, excellentRate: 0, lowRate: 0 };
        });
    },
    // 学情趋势预警
    getTeacherTrends(plan_id){
        Http.teacherTrends({plan_id:plan_id}).then((res) => {
          this.trendsData = res.data || { up: 0, down: 0, stable: 0 };
        });
    },
    // 学情诊断
    getTeacherDiagnosis(plan_id){
        Http.teacherDiagnosis({plan_id:plan_id}).then((res) => {
          this.diagnosisData = res.data || { status: '暂无数据', statusColor: '#64748B', summary: '', suggestion: '' };
        });
    },
    // 题型短板TOP3
    getTeacherWeakTop3(plan_id){
        Http.teacherWeakTop3({plan_id:plan_id}).then((res) => {
          this.top3Data = res.data || [
            { name: '暂无数据', score: 0, color: '#64748B' },
            { name: '暂无数据', score: 0, color: '#64748B' },
            { name: '暂无数据', score: 0, color: '#64748B' }
          ];
        });
    },
    // 班级动态
    getTeacherStats(plan_id){
        Http.teacherStats({plan_id:plan_id}).then((res) => {
          var d = res.data || { todayQuestions: 0, weeklyHours: 0, activeStudents: 0, trend: '+0%' };
          this.dynamics = [
            { value: String(d.todayQuestions), delta: d.trend, label: '今日完成题目' },
            { value: String(d.weeklyHours), delta: null, label: '本周学习时长(h)' },
            { value: String(d.activeStudents), delta: null, label: '今日活跃学生' }
          ];
        });
    },
    //等级分布
    getTeacherLevelDistribution(plan_id){
        Http.teacherLevelDistribution({plan_id:plan_id}).then((res) => {
          this.levelData = res.data || { distribution: [0, 0, 0, 0, 0, 0, 0], avgLevel: 0 };
          this.$nextTick(() => {
            this.initLevelChart()
          })
        });
    },

    //题型薄弱点热力图
    getTeacherQuestionScores(plan_id){
        Http.teacherQuestionScores({plan_id:plan_id}).then((res) => {
          this.weakData = res.data || [];
          var scoreColor = function (v) {
            if (v >= 80) return 'green'
            if (v >= 70) return 'orange'
            if (v >= 60) return 'gold'
            return 'red'
          };
          this.abilities = this.weakData.map(function (item) {
            return {
              name: item.name,
              score: item.value,
              color: scoreColor(item.value),
              bars: Math.round(item.value / 10)
            };
          });
          this.$nextTick(() => {
            this.initWeakChart()
          })
        });
    },
    //四维雷达图
    getTeacherRadar(plan_id){
        Http.teacherRadar({plan_id:plan_id}).then((res) => {
          var list = res.data || [];
          this.radarData = list;
          this.radarDims = list.map(function (item) { return item.name });
          this.radarBaseValues = list.map(function (item) { return item.value });
          this.radarCompareValues = [];
          this.updateRadarChart()
        });
    },

    //学生分层
    getTeacherStudentList(plan_id){
        Http.teacherStudentList({plan_id:plan_id}).then((res) => {
          var d = res.data || { emergency: [], improve: [], steady: [], excellent: [] };
          var tierMap = {
            emergency: { title: '紧急关注', tagType: 'danger', collapsed: false, status: 'danger', studentTagType: 'danger' },
            improve: { title: '需提升', tagType: 'warning', collapsed: false, status: 'warn', studentTagType: 'warning' },
            steady: { title: '稳步前进', tagType: 'success', collapsed: true, status: 'normal', studentTagType: 'success' },
            excellent: { title: '优秀', tagType: 'success', collapsed: true, status: 'normal', studentTagType: 'success' }
          };
          var mapStudent = function (s, tierName) {
            var m = tierMap[tierName] || { status: 'normal', studentTagType: 'info' };
            return {
              id: s.id,
              name: s.name,
              avatar: s.name ? s.name.charAt(0) : '',
              score: s.score,
              tag: s.tag,
              tagType: m.studentTagType,
              level: 'Lv' + s.level,
              status: m.status,
              trend: [],
              scores: []
            };
          };
          var self = this;
          this.studentTiers = ['emergency', 'improve', 'steady', 'excellent'].map(function (tierName) {
            var m = tierMap[tierName];
            var list = d[tierName] || [];
            return {
              title: m.title,
              tagType: m.tagType,
              collapsed: m.collapsed,
              students: list.map(function (s) { return mapStudent(s, tierName) })
            };
          });
        });
    },

    handleResize() {
      this.charts.forEach(function (c) { if (c) c.resize() })
      if (this.studentTrendChart) this.studentTrendChart.resize()
    },

    toggleTier(tier) {
      tier.collapsed = !tier.collapsed
    },

    openStudentDetail(s, i) {
      this.currentStudent = Object.assign({}, s, { avatarIdx: (i % 9) + 1 })
      this.studentDialogVisible = true
      var self = this
      Http.teacherStudentDetail({student_id:s.id}).then((res) => {
        var d = res.data || {};
        var tierStatus = {
          emergency: 'danger',
          improve: 'warn',
          steady: 'normal',
          excellent: 'normal'
        };
        var scoreColor = function (v) {
          if (v >= 80) return '#3ad29a'
          if (v >= 70) return '#66d98f'
          if (v >= 60) return '#ffc94d'
          if (v >= 50) return '#ff8a3d'
          return '#ff4d6d'
        };
        var scores = (d.questionScores || []).map(function (item) {
          return { name: item.name, score: item.score, color: scoreColor(item.score) }
        });
        self.currentStudent = {
          id: s.id,
          avatarIdx: (i % 9) + 1,
          avatar: d.name ? d.name.charAt(0) : s.avatar,
          name: d.name || s.name,
          className: d.className || '',
          score: d.score != null ? d.score : s.score,
          level: 'Lv' + (d.level || 1),
          tag: d.tag || s.tag,
          status: tierStatus[d.tier] || 'normal',
          tierLabel: d.tierLabel || '',
          scores: scores,
          trend: d.trend || []
        };
        self.$nextTick(() => {
          self.initStudentTrendChart(self.currentStudent.trend)
        })
      });
    },

    closeStudentDialog() {
      this.studentDialogVisible = false
      if (this.studentTrendChart) {
        this.studentTrendChart.dispose()
        this.studentTrendChart = null
      }
    },

    initStudentTrendChart(trendData) {
      var dom = this.$refs.studentTrendChart
      if (!dom) return
      trendData = (trendData && trendData.length > 0) ? trendData : [0, 0, 0, 0, 0, 0, 0]
      // Dispose old chart
      if (this.studentTrendChart) {
        this.studentTrendChart.dispose()
        this.studentTrendChart = null
      }
      var chart = echarts.init(dom)
      var dates = ['8-24', '8-25', '8-26', '8-27', '8-28', '8-29', '8-30']
      var colors = {
        normal: '#00e676',
        warn: '#ffb400',
        danger: '#ff4d6d'
      }
      var status = this.currentStudent.status || 'normal'
      var mainColor = colors[status] || '#00d4ff'
      // hex -> rgba 工具函数
      var hexToRgba = function (hex, alpha) {
        var clean = hex.replace('#', '')
        var r = parseInt(clean.substring(0, 2), 16)
        var g = parseInt(clean.substring(2, 4), 16)
        var b = parseInt(clean.substring(4, 6), 16)
        return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')'
      }
      chart.setOption({
        backgroundColor: 'transparent',
        grid: { left: 45, right: 20, top: 15, bottom: 30 },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.12)' } },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(240,244,255,0.55)', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          min: Math.max(0, Math.min.apply(null, trendData) - 10),
          max: Math.min(100, Math.max.apply(null, trendData) + 10),
          splitNumber: 5,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(240,244,255,0.45)', fontSize: 10 },
          splitLine: { lineStyle: { type: 'dashed', color: 'rgba(255,255,255,0.07)' } }
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10,14,39,0.9)',
          borderColor: 'rgba(255,255,255,0.12)',
          textStyle: { color: '#f0f4ff', fontSize: 12 }
        },
        series: [{
          type: 'line',
          data: trendData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: mainColor, width: 2 },
          itemStyle: { color: mainColor, borderColor: '#0a0e27', borderWidth: 2 },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: hexToRgba(mainColor, 0.28) },
                { offset: 1, color: 'rgba(0,0,0,0)' }
              ]
            }
          }
        }]
      })
      this.studentTrendChart = chart
    },

    initLevelChart() {
      var dom = this.$refs.levelChart
      if (!dom) return
      var existing = echarts.getInstanceByDom(dom)
      if (existing) {
        existing.dispose()
        var idx = this.charts.indexOf(existing)
        if (idx !== -1) this.charts.splice(idx, 1)
      }
      var chart = echarts.init(dom)
      this.charts.push(chart)
      var d = this.levelData
      var dist = (d && d.distribution) ? d.distribution : [0, 0, 0, 0, 0, 0, 0]
      var avg = (d && d.avgLevel) ? d.avgLevel : 0
      var maxVal = Math.max.apply(null, dist)
      var yMax = Math.max(maxVal + 2, 10)
      chart.setOption({
        backgroundColor: 'transparent',
        grid: { left: 35, right: 20, top: 20, bottom: 25 },
        xAxis: {
          type: 'category',
          data: ['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5', 'Lv6', 'Lv7'],
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.12)' } },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(240,244,255,0.55)', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          min: 0, max: yMax, splitNumber: 6,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(240,244,255,0.45)', fontSize: 10 },
          splitLine: { lineStyle: { type: 'dashed', color: 'rgba(255,255,255,0.07)' } }
        },
        series: [{
          type: 'bar',
          data: dist,
          barWidth: 42,
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#00d4ff' },
                { offset: 1, color: 'rgba(0,212,255,0.3)' }
              ]
            },
            borderRadius: [4, 4, 0, 0]
          },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: '#ffc94d', width: 1 },
            label: {
              formatter: '平均 ' + avg,
              color: '#ffc94d',
              fontSize: 11,
              position: 'insideEndTop'
            },
            data: [{ yAxis: avg }]
          }
        }]
      })
    },

    initWeakChart() {
      var dom = this.$refs.weakChart
      if (!dom) return
      var existing = echarts.getInstanceByDom(dom)
      if (existing) {
        existing.dispose()
        var idx = this.charts.indexOf(existing)
        if (idx !== -1) this.charts.splice(idx, 1)
      }
      var chart = echarts.init(dom)
      this.charts.push(chart)
      var list = this.weakData || []
      var names = list.map(function (item) { return item.name })
      var scoreColor = function (v) {
        if (v >= 80) return '#3ad29a'
        if (v >= 70) return '#66d98f'
        if (v >= 60) return '#ffc94d'
        if (v >= 50) return '#ff8a3d'
        return '#ff4d6d'
      }
      var seriesData = list.map(function (item) {
        return { value: item.value, itemStyle: { color: scoreColor(item.value) } }
      })
      chart.setOption({
        backgroundColor: 'transparent',
        grid: { left: 85, right: 35, top: 15, bottom: 25 },
        xAxis: {
          type: 'value',
          min: 0, max: 100, splitNumber: 5,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(240,244,255,0.45)', fontSize: 10 },
          splitLine: { lineStyle: { type: 'dashed', color: 'rgba(255,255,255,0.06)' } }
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: names,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(240,244,255,0.7)', fontSize: 11 }
        },
        series: [{
          type: 'bar',
          data: seriesData,
          barWidth: 14,
          itemStyle: { borderRadius: [0, 4, 4, 0] }
        }]
      })
    },

    initRadarChart() {
      var dom = this.$refs.radarChart
      if (!dom) return
      var chart = echarts.init(dom)
      this.radarChart = chart
      this.updateRadarChart()
      this.charts.push(chart)
    },

    updateRadarChart() {
      if (!this.radarChart) return
      var displayValues = this.radarBaseValues.slice()
      if (this.activeRadarDim) {
        var dimIdx = this.radarDims.indexOf(this.activeRadarDim)
        if (dimIdx >= 0 && this.activeRadarScore != null) {
          displayValues[dimIdx] = this.activeRadarScore
        }
      }
      var self = this
      var seriesData = [
        {
          value: displayValues,
          name: '当前能力',
          symbol: 'circle',
          symbolSize: function (val, params) {
            var idx = params.dimensionIndex
            return idx >= 0 && self.radarDims[idx] === self.activeRadarDim ? 10 : 5
          },
          areaStyle: { color: 'rgba(0,212,255,0.22)' },
          lineStyle: { color: '#00d4ff', width: 2 },
          itemStyle: {
            color: function (val, params) {
              var idx = params ? params.dimensionIndex : -1
              return idx >= 0 && self.radarDims[idx] === self.activeRadarDim ? '#ffc94d' : '#00d4ff'
            }
          }
        }
      ]
      if (this.radarCompareValues && this.radarCompareValues.length > 0) {
        seriesData.push({
          value: this.radarCompareValues,
          name: '对比能力',
          symbol: 'none',
          areaStyle: { color: 'rgba(51,221,255,0.15)' },
          lineStyle: { color: '#33ddff', width: 1.5, type: 'dashed' },
          itemStyle: { color: '#33ddff' }
        })
      }
      this.radarChart.setOption({
        backgroundColor: 'transparent',
        legend: {
          bottom: 8,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
          textStyle: { color: 'rgba(240,244,255,0.7)', fontSize: 11 },
          data: this.radarCompareValues && this.radarCompareValues.length > 0 ? ['当前能力', '对比能力'] : ['当前能力']
        },
        radar: {
          indicator: this.radarDims.map(function (d) {
            return { name: d, max: 100 }
          }),
          center: ['50%', '48%'],
          radius: '68%',
          axisName: {
            formatter: function (name) {
              if (name === self.activeRadarDim) {
                return '{active|' + name + '}'
              }
              return name
            },
            rich: {
              active: {
                color: '#ffc94d',
                fontWeight: 'bold',
                fontSize: 13,
                padding: [0, 0, 0, 0]
              }
            },
            color: 'rgba(240,244,255,0.8)',
            fontSize: 12
          },
          splitLine: {
            lineStyle: { color: 'rgba(255,255,255,0.1)' }
          },
          splitArea: {
            areaStyle: { color: ['rgba(0,212,255,0.03)', 'rgba(0,212,255,0.06)'] }
          },
          axisLine: {
            lineStyle: { color: 'rgba(255,255,255,0.1)' }
          }
        },
        series: [{
          type: 'radar',
          emphasis: { focus: 'self' },
          data: seriesData
        }]
      })
    },

    onTop3Click(item, i) {
      if (this.activeTop3Idx === i) {
        this.activeTop3Idx = -1
        this.activeRadarDim = ''
        this.activeRadarScore = null
      } else {
        this.activeTop3Idx = i
        this.activeAbilityName = ''
        this.activeRadarDim = item.dim
        this.activeRadarScore = item.score
      }
      this.updateRadarChart()
    },

    onAbilityClick(a) {
      if (this.activeAbilityName === a.name) {
        this.activeAbilityName = ''
        this.activeRadarDim = ''
        this.activeRadarScore = null
      } else {
        this.activeAbilityName = a.name
        this.activeTop3Idx = -1
        this.activeRadarDim = a.dim
        this.activeRadarScore = a.score
      }
      this.updateRadarChart()
    }
  }
}
</script>

<style>
/* ============ PAGE BG ============ */
.td-page {
  margin: 0; padding: 0;
  background: #0a0e27;
  color: #f0f4ff;
  -webkit-font-smoothing: antialiased;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', 'Segoe UI', sans-serif;
}
.td-page {
  min-height: 100vh;
  background: #0a0e27 !important;
  color: #f0f4ff !important;
}
::-webkit-scrollbar { display: none; width: 0; height: 0; }

/* ============ THEME ============ */
.td-page {
  --cyan: #00d4ff;
  --green: #00e676;
  --green-soft: #35e0a1;
  --gold: #ffb400;
  --gold-soft: #ffc94d;
  --orange: #ff7a3d;
  --red: #ff4d6d;
  --red-soft: #ff5269;
  --text-pri: #f0f4ff;
  --text-sec: rgba(240,244,255,0.65);
  --text-muted: rgba(240,244,255,0.38);
  --text-dim: rgba(240,244,255,0.25);
  --bg-card: rgba(255,255,255,0.04);
  --border-card: 1px solid rgba(255,255,255,0.08);

  min-height: 100vh;
  width: 100%;
  background:
    radial-gradient(ellipse at 12% -5%, rgba(0,212,255,0.10) 0%, transparent 55%),
    radial-gradient(ellipse at 92% 3%, rgba(124,77,255,0.10) 0%, transparent 50%),
    #0a0e27;
  color: var(--text-pri);
  overflow: hidden;
}

/* ============ HEADER ============ */
.td-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: rgba(10,14,39,0.55);
}
.td-header-left {
  display: flex;
  flex-direction: row;
  align-items: baseline;
}
.td-brand-title { font-size: 19px; font-weight: 700; color: var(--text-pri); letter-spacing: 0.5px; }
.td-brand-sep { color: rgba(255,255,255,0.25); font-size: 17px; margin: 0 6px; }
.td-brand-sub { font-size: 17px; font-weight: 500; color: rgba(240,244,255,0.75); letter-spacing: 0.5px; }
.td-header-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}
.td-header-date { font-size: 13px; color: var(--text-muted); font-family: 'SF Mono', Menlo, Consolas, monospace; }
.td-back-btn { background: rgba(0,212,255,0.08) !important; border-color: rgba(0,212,255,0.4) !important; color: #00d4ff !important; margin-right: 15px !important; }
.td-back-btn:hover, .td-back-btn:focus { background: rgba(0,212,255,0.16) !important; border-color: rgba(0,212,255,0.7) !important; color: #33ddff !important; }
.td-back-btn i { color: #00d4ff !important; }

/* ============ GRID ============ */
.td-grid {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  width: 100%;
  box-sizing: border-box;
}
.td-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.td-col-left  { width: 24%; flex: 0 0 24%; }
.td-col-mid   { width: 50%; flex: 0 0 50%; }
.td-col-right { width: 26%; flex: 0 0 26%; }

/* ============ CARD ============ */
.td-card {
  background: var(--bg-card);
  border: var(--border-card);
  border-radius: 12px;
  padding: 12px 13px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}
.td-card-chart { padding: 12px 10px 10px 10px; }
.td-title {
  text-align: left;
  font-weight: 600;
  color: var(--text-pri);
  margin: 0 0 11px;
  letter-spacing: 0.3px;
  display: block;
  font-size: 15px;
}
.td-title-sm { margin-bottom: 8px; }

/* Chart containers */
.chart-box { width: 100%; }
.level-box { height: 240px; }
.weak-box  { height: 300px; }
.radar-box { height: 380px; }

/* ============ LEFT COLUMN ============ */

/* Class selector card */
.td-card-class { padding: 12px 13px 13px; }
.td-class-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.td-class-school { font-size: 12px; color: var(--text-muted); }
.td-class-arrow { display: inline-flex; align-items: center; padding: 2px; }
.td-class-main {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.td-class-name { font-size: 19px; font-weight: 700; color: var(--text-pri); letter-spacing: 0.3px; }
.td-class-count {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 3px 8px 3px 6px;
  border-radius: 9999px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  flex-shrink: 0;
}
.td-class-count-txt { font-size: 11px; color: rgba(240,244,255,0.6); font-family: Menlo, Consolas, monospace; font-weight: 500; }

/* Core indicators */
.core-grid { display: flex; flex-direction: row; flex-wrap: wrap; gap: 7px; width: 100%; }
.core-item {
  flex: 1 1 calc(50% - 3.5px);
  min-width: calc(50% - 3.5px);
  max-width: calc(50% - 3.5px);
  border-radius: 9px;
  padding: 9px 10px 10px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}
.core-item::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: currentColor;
  opacity: 0.7;
}
.core-cyan  { background: rgba(0,212,255,0.08);  color: var(--cyan); }
.core-green { background: rgba(0,230,118,0.08); color: var(--green); }
.core-gold  { background: rgba(255,180,0,0.08); color: var(--gold); }
.core-red   { background: rgba(255,77,109,0.08); color: var(--red); }
.core-val { display: block; font-size: 23px; font-weight: 700; font-family: Menlo, Consolas, monospace; line-height: 1.1; margin-bottom: 3px; letter-spacing: 0.3px; }
.core-lbl { display: block; font-size: 11px; color: var(--text-sec); font-weight: 500; }

/* Trend alerts */
.trend-row { display: flex; flex-direction: row; gap: 7px; width: 100%; margin-bottom: 12px; }
.trend-item {
  flex: 1 1 33.3%;
  min-width: 0;
  border-radius: 10px;
  padding: 9px 7px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 7px;
}
.trend-up   { background: rgba(0,230,118,0.06); border: 1px solid rgba(0,230,118,0.15); }
.trend-down { background: rgba(255,77,109,0.06); border: 1px solid rgba(255,77,109,0.15); }
.trend-flat { background: rgba(255,180,0,0.06); border: 1px solid rgba(255,180,0,0.15); }
.trend-icon { width: 32px; height: 32px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.trend-up   .trend-icon { background: rgba(0,230,118,0.12); }
.trend-down .trend-icon { background: rgba(255,77,109,0.12); }
.trend-flat .trend-icon { background: rgba(255,180,0,0.12); }
.trend-text { display: flex; flex-direction: column; min-width: 0; }
.trend-lbl { font-size: 11px; color: var(--text-sec); line-height: 1.3; margin-bottom: 2px; }
.trend-num { font-size: 16px; font-weight: 700; font-family: Menlo, Consolas, monospace; line-height: 1.1; }
.trend-num-up   { color: var(--green); }
.trend-num-down { color: var(--red); }
.trend-num-flat { color: var(--gold); }

/* Diagnosis */
.diag-block { border-radius: 10px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); padding: 10px; }
.diag-band { display: flex; flex-direction: row; align-items: center; gap: 6px; padding: 3px 10px 3px 6px; border-radius: 5px; background: linear-gradient(90deg, rgba(255,122,61,0.22), rgba(255,180,0,0.18)); border: 1px solid rgba(255,180,0,0.25); margin-bottom: 8px; width: 100%; box-sizing: border-box; }
.diag-dot { width: 6px; height: 6px; border-radius: 9999px; background: var(--orange); box-shadow: 0 0 6px rgba(255,122,61,0.8); flex-shrink: 0; }
.diag-band-txt { font-size: 14px; font-weight: 600; color: var(--orange); letter-spacing: 0.5px; }
.diag-seg { margin-bottom: 8px; display: block; }
.diag-seg-txt { font-size: 12px; color: var(--text-sec); line-height: 1.4; }
.diag-hi  { color: var(--green); font-weight: 600; }
.diag-lo  { color: var(--red);   font-weight: 600; }
.diag-mid { color: var(--gold);  font-weight: 600; }
.diag-hint { display: flex; flex-direction: row; align-items: flex-start; gap: 6px; padding: 7px 8px; border-radius: 7px; background: rgba(0,212,255,0.08); border: 1px solid rgba(0,212,255,0.18); }
.diag-hint-dot { width: 6px; height: 6px; border-radius: 9999px; background: var(--cyan); margin-top: 6px; box-shadow: 0 0 5px rgba(0,212,255,0.8); flex-shrink: 0; }
.diag-hint-txt { font-size: 11px; color: rgba(200,240,255,0.85); line-height: 1.5; display: block; }

/* TOP 3 weakness */
.top3-list { display: flex; flex-direction: column; gap: 10px; width: 100%; }
.top3-item { display: flex; flex-direction: row; align-items: center; gap: 8px; width: 100%; box-sizing: border-box; padding: 7px 6px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; transition: all 0.2s; }
.top3-item:hover { background: rgba(255,255,255,0.04); }
.top3-item-active { background: rgba(255,201,77,0.1) !important; border-color: rgba(255,201,77,0.45) !important; box-shadow: 0 0 12px rgba(255,201,77,0.15); }
.top3-rank { width: 18px; height: 18px; border-radius: 5px; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.top3-rank-1 { background: linear-gradient(135deg,#ff4d6d,#ff7a3d); color: #fff; }
.top3-rank-2 { background: linear-gradient(135deg,#ff6b88,#ff9a3d); color: #fff; }
.top3-rank-3 { background: linear-gradient(135deg,#ffb400,#ffd24d); color: #0a0e27; }
.top3-name { font-size: 12px; color: var(--text-pri); font-weight: 500; width: 62px; flex-shrink: 0; }
.top3-progress { flex: 1; min-width: 0; }
.top3-score { font-size: 12px; font-weight: 700; font-family: Menlo, Consolas, monospace; flex-shrink: 0; width: 22px; text-align: right; }
.top3-score-red  { color: var(--red); }
.top3-score-gold { color: var(--gold); }

/* Dynamics */
.dyn-grid { display: flex; flex-direction: row; flex-wrap: wrap; gap: 7px; width: 100%; }
.dyn-item {
  flex: 1 1 calc(50% - 3.5px);
  min-width: calc(50% - 3.5px);
  max-width: calc(50% - 3.5px);
  border-radius: 9px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.05);
  padding: 9px 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: 64px;
}
.dyn-val-row { display: flex; flex-direction: row; align-items: baseline; gap: 5px; margin-bottom: 3px; width: 100%; }
.dyn-val-big { font-size: 24px; line-height: 1.1; margin-bottom: 3px; }
.dyn-val { font-weight: 700; font-family: Menlo, Consolas, monospace; color: var(--cyan); }
.dyn-delta { font-size: 11px; font-family: Menlo, Consolas, monospace; color: var(--green); font-weight: 600; }
.dyn-lbl { font-size: 11px; color: var(--text-muted); display: block; width: 100%; line-height: 1.3; }

/* ============ RIGHT COLUMN ============ */

/* Ability map */
.am-list { max-height: 240px; overflow-y: auto; padding-right: 4px; }
.am-list::-webkit-scrollbar { width: 4px; }
.am-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
.am-block { width: 100%; padding: 9px 10px; border-radius: 9px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); margin-bottom: 10px; cursor: pointer; transition: all 0.2s; }
.am-block:hover { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); }
.am-block-active { background: rgba(255,201,77,0.1) !important; border-color: rgba(255,201,77,0.45) !important; box-shadow: 0 0 12px rgba(255,201,77,0.15); }
.am-block:last-child { margin-bottom: 0; }
.am-head { display: flex; flex-direction: row; align-items: baseline; justify-content: space-between; margin-bottom: 6px; }
.am-name { font-size: 12px; color: var(--text-pri); font-weight: 500; }
.am-score { font-size: 13px; font-weight: 700; font-family: Menlo, Consolas, monospace; }
.am-score-green  { color: var(--green-soft); }
.am-score-orange { color: var(--gold-soft); }
.am-score-gold   { color: var(--gold-soft); }
.am-score-red    { color: var(--red-soft); }
.am-bars { display: flex; flex-direction: row; gap: 3px; margin-bottom: 6px; }
.am-bar { flex: 1; min-width: 0; height: 9px; border-radius: 2px; background: rgba(255,255,255,0.06); }
.am-bar-green  { background: linear-gradient(180deg,#66d98f,#35e0a1); }
.am-bar-orange { background: linear-gradient(180deg,#ffcc33,#ffb400); }
.am-bar-gold   { background: linear-gradient(180deg,#ffd24d,#ffae14); }
.am-bar-red    { background: linear-gradient(180deg,#ff7a3d,#ff4d6d); }
.am-foot { display: flex; flex-direction: row; align-items: center; gap: 8px; }
.am-lvtag { border-radius: 4px !important; font-size: 10px !important; font-weight: 600; font-family: Menlo, Consolas, monospace; }
.am-skill { font-size: 11px; color: var(--text-muted); font-weight: 500; }

/* Student tier list */
.stu-card { max-height: calc(100vh - 380px); overflow-y: auto; }
.stu-card::-webkit-scrollbar { width: 4px; }
.stu-card::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
.td-tier-section { border-bottom: 1px solid rgba(255,255,255,0.05); }
.td-tier-section:last-child { border-bottom: none; }
.td-tier-head { display: flex; flex-direction: row; align-items: center; justify-content: space-between; padding: 10px 2px; cursor: pointer; transition: background 0.2s; border-radius: 6px; }
.td-tier-head:hover { background: rgba(255,255,255,0.03); }
.td-tier-head-collapsed { padding-bottom: 10px; }
.td-tier-title-wrap { display: flex; flex-direction: row; align-items: center; gap: 6px; }
.td-tier-arrow { display: inline-flex; align-items: center; transition: transform 0.3s ease; }
.td-tier-arrow-rotated { transform: rotate(-90deg); }
.td-tier-title { font-size: 15px; font-weight: 600; color: var(--text-pri); }
.td-student-list { display: flex; flex-direction: column; gap: 0; overflow: hidden; margin: 0 -5px; padding: 0 5px; }

/* Collapse transition */
.tier-slide-enter-active, .tier-slide-leave-active { transition: all 0.3s ease; max-height: 600px; overflow: hidden; }
.tier-slide-enter, .tier-slide-leave-to { max-height: 0; opacity: 0; }
.td-stu-item { display: flex; flex-direction: row; align-items: center; gap: 10px; padding: 10px 7px; border-radius: 0; background: transparent; border-bottom: 1px solid rgba(255,255,255,0.05); }
.td-stu-item:last-child { border-bottom: none; }
.td-stu-avatar { width: 33px; height: 33px; border-radius: 9999px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 6px rgba(0,0,0,0.25); }
.td-stu-avatar-1 { background: linear-gradient(135deg,#448aff,#2962ff); }
.td-stu-avatar-2 { background: linear-gradient(135deg,#00bcd4,#00acc1); }
.td-stu-avatar-3 { background: linear-gradient(135deg,#7c4dff,#651fff); }
.td-stu-avatar-4 { background: linear-gradient(135deg,#26c6da,#00acc1); }
.td-stu-avatar-5 { background: linear-gradient(135deg,#2196f3,#1976d2); }
.td-stu-avatar-6 { background: linear-gradient(135deg,#5c6bc0,#3949ab); }
.td-stu-avatar-7 { background: linear-gradient(135deg,#42a5f5,#1e88e5); }
.td-stu-avatar-8 { background: linear-gradient(135deg,#29b6f6,#0288d1); }
.td-stu-avatar-9 { background: linear-gradient(135deg,#26a69a,#00897b); }
.td-stu-avatar-txt { font-size: 13px; font-weight: 600; color: #fff; }
.td-stu-mid { display: flex; flex-direction: column; min-width: 0; gap: 8px; }
.td-stu-name { font-size: 12px; font-weight: 600; color: var(--text-pri); line-height: 1.2; }
.td-stu-score { font-size: 14px; font-weight: 700; font-family: Menlo, Consolas, monospace; flex-shrink: 0; width: 28px; text-align: right; margin-left: auto; }
.td-stu-score-red  { color: var(--red); }
.td-stu-score-gold { color: var(--gold); }
.td-stu-detail { color: #00d4ff !important; background: rgba(0,212,255,0.08); border-radius: 4px; padding: 3px 5px; font-size: 11px; }

/* ============ Element UI Dark Theme Overrides ============ */
/* el-select */
.class-select .el-input__inner {
  background-color: rgba(255,255,255,0.06) !important;
  border-color: rgba(255,255,255,0.1) !important;
  color: rgba(240,244,255,0.85) !important;
  height: 28px !important;
  line-height: 28px !important;
  font-size: 12px !important;
}
.class-select .el-input__icon { color: rgba(240,244,255,0.4) !important; line-height: 28px !important; }

/* el-select dropdown (dark) */
.dark-popper {
  background-color: #1a1e3a !important;
  border: 1px solid rgba(255,255,255,0.1) !important;
}
.dark-popper .el-select-dropdown__item {
  color: rgba(240,244,255,0.7) !important;
}
.dark-popper .el-select-dropdown__item.hover,
.dark-popper .el-select-dropdown__item:hover {
  background-color: rgba(0,212,255,0.1) !important;
  color: #00d4ff !important;
}
.dark-popper .popper__arrow::after {
  border-bottom-color: #1a1e3a !important;
}
.dark-popper .popper__arrow {
  border-bottom-color: rgba(255,255,255,0.1) !important;
}

/* el-progress */
.top3-progress .el-progress-bar__outer {
  background-color: rgba(255,255,255,0.05) !important;
  border-radius: 9999px !important;
}
.top3-progress .el-progress-bar__inner {
  border-radius: 9999px !important;
}

/* el-tag dark overrides */
.el-tag--mini { font-size: 10px; height: auto; line-height: 1.5; padding: 1px 7px; border-radius: 4px; }
.el-tag--dark.el-tag--danger { background-color: rgba(255,77,109,0.15); border-color: rgba(255,77,109,0.3); color: #ff4d6d; }
.el-tag--dark.el-tag--warning { background-color: rgba(255,180,0,0.15); border-color: rgba(255,180,0,0.3); color: #ffb400; }
.el-tag--dark.el-tag--success { background-color: rgba(53,224,161,0.15); border-color: rgba(53,224,161,0.3); color: #35e0a1; }

/* el-button text override */
.td-stu-detail.el-button--text { color: #00d4ff !important; }
.td-stu-detail.el-button--text:hover { background-color: rgba(0,212,255,0.15) !important; }

/* ============ STUDENT DETAIL DIALOG ============ */
.student-detail-dialog {
  background: linear-gradient(135deg, #1a1f3a 0%, #141a35 100%) !important;
  border: 1px solid rgba(255,255,255,0.08) !important;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) !important;
}
.student-detail-dialog .el-dialog__header { display: none; }
.student-detail-dialog .el-dialog__body { padding: 0; color: #f0f4ff; }

/* Dialog header */
.sd-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  position: relative;
  background: linear-gradient(180deg, rgba(0,212,255,0.06) 0%, transparent 100%);
}
.sd-avatar {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
}
.sd-avatar-txt { font-size: 22px; font-weight: 700; color: #fff; }
.sd-avatar-default { background: linear-gradient(135deg, #00d4ff, #0099cc); }
.sd-head-info { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.sd-head-row1 { display: flex; flex-direction: row; align-items: baseline; gap: 10px; }
.sd-name { font-size: 20px; font-weight: 700; color: #f0f4ff; }
.sd-class { font-size: 13px; color: rgba(240,244,255,0.5); }
.sd-head-row2 { display: flex; flex-direction: row; align-items: center; gap: 10px; flex-wrap: wrap; }
.sd-score-label { font-size: 12px; color: rgba(240,244,255,0.45); }
.sd-score-val { font-size: 20px; font-weight: 700; font-family: Menlo, Consolas, monospace; }
.sd-score-red  { color: #ff4d6d; }
.sd-score-gold { color: #ffb400; }
.sd-score-label { color: rgba(240,244,255,0.5); font-size: 12px; }
.sd-level-dot { width: 4px; height: 4px; border-radius: 9999px; background: rgba(240,244,255,0.35); }
.sd-level { font-size: 12px; color: rgba(240,244,255,0.65); font-family: Menlo, Consolas, monospace; font-weight: 600; }
.sd-status-dot { width: 6px; height: 6px; border-radius: 9999px; }
.sd-status-normal { background: #00e676; box-shadow: 0 0 6px rgba(0,230,118,0.6); }
.sd-status-warn { background: #ffb400; box-shadow: 0 0 6px rgba(255,180,0,0.6); }
.sd-status-danger { background: #ff4d6d; box-shadow: 0 0 6px rgba(255,77,109,0.6); }
.sd-status { font-size: 12px; font-weight: 500; }
.sd-status-txt-normal { color: #00e676; }
.sd-status-txt-warn { color: #ffb400; }
.sd-status-txt-danger { color: #ff4d6d; }
.sd-close {
  position: absolute;
  top: 18px;
  right: 20px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: rgba(255,255,255,0.04);
  transition: all 0.2s;
}
.sd-close:hover { background: rgba(255,77,109,0.2); }
.sd-close:hover svg { stroke: #ff4d6d; }

/* Dialog sections */
.sd-section { padding: 18px 24px; }
.sd-section-title { font-size: 14px; font-weight: 600; color: #f0f4ff; margin-bottom: 14px; letter-spacing: 0.3px; }

/* Score list */
.sd-score-list { display: flex; flex-direction: column; gap: 12px; }
.sd-score-item { display: flex; flex-direction: row; align-items: center; gap: 12px; }
.sd-score-name { font-size: 13px; color: rgba(240,244,255,0.8); width: 72px; flex-shrink: 0; }
.sd-score-bar-wrap { flex: 1; position: relative; height: 8px; }
.sd-score-bar-bg { position: absolute; top: 0; left: 0; right: 0; height: 100%; border-radius: 9999px; background: rgba(255,255,255,0.07); }
.sd-score-bar-fill { position: absolute; top: 0; left: 0; height: 100%; border-radius: 9999px; transition: width 0.4s ease; }
.sd-score-num { font-size: 14px; font-weight: 700; font-family: Menlo, Consolas, monospace; width: 36px; text-align: right; flex-shrink: 0; }

/* Trend chart */
.sd-trend-chart { height: 180px; width: 100%; }

/* Avatar gradient helpers (reuse student avatar colors) */
.sd-avatar-default { background: linear-gradient(135deg, #00d4ff, #0099cc); }
.sd-avatar-1  { background: linear-gradient(135deg, #448aff, #2962ff); }
.sd-avatar-2  { background: linear-gradient(135deg, #00bcd4, #00acc1); }
.sd-avatar-3  { background: linear-gradient(135deg, #7c4dff, #651fff); }
.sd-avatar-4  { background: linear-gradient(135deg, #26c6da, #00acc1); }
.sd-avatar-5  { background: linear-gradient(135deg, #2196f3, #1976d2); }
.sd-avatar-6  { background: linear-gradient(135deg, #5c6bc0, #3949ab); }
.sd-avatar-7  { background: linear-gradient(135deg, #42a5f5, #1e88e5); }
.sd-avatar-8  { background: linear-gradient(135deg, #29b6f6, #0288d1); }
.sd-avatar-9  { background: linear-gradient(135deg, #26a69a, #00897b); }
</style>
